import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';
import { checkCampaignActive, checkCampaignCode } from '../api/campaign';
import { searchCoupon } from '../api/coupon';
import { getPlanMemberSettings } from '../api/plan';
import {
  cancelMembership,
  findOriginalPlan,
  getPortalSession,
  getSessionId,
  registerPayment,
} from '../api/user';
import { ConfirmModal, NoticeModal } from '../components/Modals';
import UserPageLayout from '../components/UserPageLayout';
import { useUser } from '../hooks/useUser.js';
import { useUserPayments } from '../hooks/useUserPayments.js';
import { formatDateShort } from '../utils/dateFormat.js';
import { formatLinkageIdentifier } from '../utils/paymentFormat.js';
import {
  getCampaignCode,
  getCompanyId,
  setCampaignCode,
  setCompanyId,
} from '../utils/storage.js';
import { NOT_PURCHASED, PURCHASED, usePlans } from '../utils/usePlans.js';
import './MyPage.css';

const MyPage = () => {
  const navigate = useNavigate();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(() => () => {});
  const [couponData, setCouponData] = useState({});
  const [memberIdModalData, setMemberIdModalData] = useState(null);

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  const showConfirm = (msg, onConfirm, memberIdOptions = null) => {
    setConfirmMsg(msg);
    setOnConfirmAction(() => onConfirm);
    setMemberIdModalData(memberIdOptions);
    setConfirmOpen(true);
  };

  const {
    oneTimeOptions,
    subscriptionOptions,
    loading: plansLoading,
    getPlanName,
  } = usePlans();
  const allPrices = useMemo(
    () => [...oneTimeOptions, ...subscriptionOptions],
    [oneTimeOptions, subscriptionOptions]
  );

  // SWRフックを使用してユーザー情報と支払い情報を取得
  const { user, isLoading: userLoading } = useUser();
  const {
    payments,
    isLoading: paymentsLoading,
    mutate: mutatePayments,
  } = useUserPayments();

  const companyId = getCompanyId();
  const campaignCode = getCampaignCode();
  const [trialChecked, setTrialChecked] = useState(false);
  const [campaignStatuses, setCampaignStatuses] = useState({});

  const registerPaymentIfNeed = useCallback(
    async (price_id) => {
      if (!companyId || !price_id) return;

      const plan = allPrices.find((p) => p.price_id === price_id);
      const exclusive_plan = plan?.exclusive_plan;
      if (exclusive_plan) {
        // キャンペーンコードの存在チェック
        const checkResult = await checkCampaignCode(exclusive_plan);

        // すでに登録済みの場合に中断
        if (checkResult.exists) {
          showNotice(
            'このdアカウントではすでにキャンペーン特典を利用済みのためお申し込みいただけません'
          );
          return;
        }
      }

      // 1. 契約中(PURCHASED)の月額プランのみブロック
      const alreadyRegistered = payments.find((p) => {
        const planInfo = allPrices.find((pl) => pl.price_id === p.price_id);
        return (
          p.price_id === price_id &&
          p.is_member === PURCHASED &&
          planInfo?.interval === 'month'
        );
      });
      if (alreadyRegistered) {
        return;
      }

      // 2. 対応する有料プラン(repeat_plan_id)が契約済みの場合、
      //    無料トライアル付きプランの登録をスキップ
      if (plan?.repeat_plan_id) {
        const repeatedPlanPayment = payments.find(
          (p) => p.price_id === plan.repeat_plan_id && p.is_member === PURCHASED
        );
        if (repeatedPlanPayment) {
          return;
        }
      }

      // sessionStorage から会員ID取得（POST連携時のみ値あり）
      const linkageMemberId = sessionStorage.getItem('linkage_member_id');

      try {
        await registerPayment(companyId, price_id, linkageMemberId);

        // 保存成功後、sessionStorage クリア
        sessionStorage.removeItem('linkage_member_id');

        mutatePayments();
        setCompanyId(null);
        setCampaignCode(null);
      } catch (err) {
        if (err.response && err.response.status === 400) {
          sessionStorage.removeItem('linkage_member_id');

          // is_member=2（自動連携）のプランでPOST連携なしの場合、エラーページに遷移
          if (err.response.data?.error_type === 'invalid_access') {
            navigate('/error?errorType=invalid_access');
            return;
          }

          mutatePayments();
          setCompanyId(null);
          setCampaignCode(null);
        } else {
          console.error('Payment registration failed:', err);
        }
      }
    },
    [companyId, payments, allPrices, mutatePayments, navigate]
  );

  // handleStartMembershipをcheckTrialPopupより前に定義
  const handleStartMembership = useCallback(async (companyId, price_id, plan_name) => {
    try {
      // 会員ID設定を取得（plansテーブル由来）
      const memberSettings = await getPlanMemberSettings(price_id);

      // 既存の member_id（payments から）と POST連携の値（sessionStorage）を取得
      const payment = payments.find(p => p.price_id === price_id);
      const existingMemberId = payment?.member_id || null;
      const linkageMemberId = sessionStorage.getItem('linkage_member_id');

      // is_member の判定（1:手入力, 2:自動）
      const isMemberSetting = Number(memberSettings?.is_member) || 0;

      // 自動入力の既定値は「POST 連携 > 既存 member_id > null」の優先順
      const defaultAutoMemberId = linkageMemberId ?? existingMemberId ?? null;
      // 手入力の既定値は「既存 member_id > null」
      const defaultManualMemberId = existingMemberId ?? null;

      // モーダルに渡すオプション
      const memberIdOptions = isMemberSetting
        ? {
            isMember: true,
            validationPattern: memberSettings?.validation_type_pattern || null,
            validationType: memberSettings?.member_id_validation_type || null,
            minDigits: memberSettings?.member_id_min_digits ?? null,
            maxDigits: memberSettings?.member_id_max_digits ?? null,
            memberIdLabel: memberSettings?.member_id_label || null,
            isPostLinked: isMemberSetting === 2, // 自動入力モードのフラグ
            defaultMemberId: isMemberSetting === 2 ? defaultAutoMemberId : defaultManualMemberId,
            planName: plan_name,
          }
        : null;

      // 確認モーダルを表示
      showConfirm(
        `「${plan_name}」プランを契約します。\n契約を続行してもよろしいですか？`,
        (memberId) => {
          navigate(`/terms?member_id=${memberId ?? ''}`, {
            state: { userId: user.id, companyId, price_id },
          });
        },
        memberIdOptions
      );
    } catch (error) {
      console.error('会員ID設定取得エラー:', error);
      showConfirm(
        `「${plan_name}」プランを契約します。\n契約を続行してもよろしいですか？`,
        () => {
          navigate('/terms', { state: { userId: user.id, companyId, price_id } });
        }
      );
    }
  }, [payments, user, navigate, showConfirm]);

  const checkTrialPopup = useCallback(
    async (userPayments) => {
      if (!userPayments || userPayments.length === 0 || campaignCode === '')
        return;

      const selectedPlan = allPrices.find((plan) => plan.price_id === campaignCode);
      const price_id = selectedPlan?.repeat_plan_id;
      
      // 有料プラン（2回目）が既に契約済みかチェック
      const repeatedPlanPayment = payments.find(
        (payment) => payment.price_id === price_id && payment.is_member === PURCHASED
      );
      
      // 既に契約済みの場合は何もしない（お知らせも表示せず、マイページをそのまま表示する）
      if (repeatedPlanPayment) {
        setCampaignCode(null); // campaignCodeをクリア
        return;
      }

      // 金額表示用にプラン情報を取得
      const repeatedPlanInfo = allPrices.find((plan) => plan.price_id === price_id);
      const name = repeatedPlanInfo?.name;
      const amount = Math.floor(repeatedPlanInfo?.amount || 0);

      // Aプランが現在も契約中であれば処理を終了する
      const currentPlanPayment = payments.find(
        (p) => p.price_id === campaignCode
      );
      if (currentPlanPayment?.is_member === PURCHASED) {
        return;
      }

      // 無料トライアル済みのレコードを探す（解約済み含む）
      const trialPayment = userPayments.find(
        (p) => p.trial_ends_at !== null && p.price_id === campaignCode
      );

      if (trialPayment) {
        // 無料トライアル利用済み → 有料プラン誘導ポップアップ表示
        showConfirm(
          `無料トライアルは既にご利用済みです。\n` +
          `${name}（月額${amount}円）へのお申し込みに進みますか？`,
          () => {
            handleStartMembership(companyId, price_id, name);
          }
        );
      } else {
        // 無料トライアル未利用 → 無料プラン登録
        try {
          await registerPaymentIfNeed(campaignCode);
        } catch (err) {
          console.error('無料プラン登録に失敗しました', err);
        }
      }
    },
    [campaignCode, registerPaymentIfNeed, handleStartMembership, navigate, user, allPrices, companyId, payments, showConfirm]
  );

  const getIsSubscription = useCallback(
    (price_id) => {
      return subscriptionOptions.some((option) => option.price_id === price_id);
    },
    [subscriptionOptions]
  );

  const getAmount = useCallback(
    (price_id) => {
      const price = allPrices.find((p) => p.price_id === price_id);
      return price ? price.amount : 0;
    },
    [allPrices]
  );

  const getTrialDays = (price_id) => {
    const price = allPrices.find((p) => p.price_id === price_id);
    return price ? price.trial_days : 0;
  };

  const checkCampaignStatus = useCallback(async (price_id) => {
    try {
      const result = await checkCampaignActive(price_id);
      setCampaignStatuses((prev) => ({
        ...prev,
        [price_id]: result.is_active === 1,
      }));
    } catch (error) {
      console.error('キャンペーン有効性チェックエラー:', error);
      setCampaignStatuses((prev) => ({
        ...prev,
        [price_id]: false,
      }));
    }
  }, []);

  const [originalPlans, setOriginalPlans] = useState({});
  useEffect(() => {
    const fetchOriginalPlans = async () => {
      if (!payments.length) return;

      const newPlans = {};
      for (const p of payments) {
        try {
          const res = await findOriginalPlan(p.price_id);
          newPlans[p.price_id] = res?.original_plan || null;
        } catch (err) {
          console.error('findOriginalPlan error:', err);
          newPlans[p.price_id] = null;
        }
      }

      setOriginalPlans(newPlans);
    };

    fetchOriginalPlans();
  }, [payments]);

  const getPaymentMember = (isMember, price_id) => {
    const isSubscription = getIsSubscription(price_id);
    const isCampaignActive = campaignStatuses[price_id] !== false;

    // キャンペーンが無効な場合は常に「無効」を表示
    if (!isCampaignActive) return '無効';
    if (isMember === NOT_PURCHASED) return '契約前';
    if (isMember === PURCHASED) {
      return isSubscription ? '契約中' : '購入済み';
    }
    return '解約済み';
  };

  const handleCancelMembership = async (subscriptionId, plan_name) => {
    showConfirm(
      `「${plan_name}」プランの契約を解約します。\n解約すると次回更新日以降はご利用できません。\n本当に解約しますか？`,
      async () => {
        try {
          const sessionRes = await getSessionId(subscriptionId);
          const sessionId = sessionRes.checkout_session_id;
          await cancelMembership(subscriptionId);
          showNotice('解約が完了しました');
          mutatePayments();
          navigate(`/complete?session_id=${sessionId}`, {
            state: { status: 'cancel' },
          });
        } catch (err) {
          showNotice('解約処理に失敗しました');
        }
      }
    );
  };

  const getPaymentButtons = (
    isMember,
    campaignCode,
    linkageIdentifier,
    memberId,
    companyId,
    price_id,
    plan_name,
    isSubscription
  ) => {
    let buttonText = '';
    let buttonColor = '';
    let buttonVariant = '';
    if (isMember === NOT_PURCHASED) {
      buttonText = '契約手続きに進む';
      buttonColor = 'info';
      buttonVariant = 'outlined';
    } else if (isMember === PURCHASED) {
      buttonText = 'シリアルコード発行に進む';
      buttonColor = 'info';
      buttonVariant = 'outlined';
    } else {
      buttonText = 'シリアルコード停止に進む';
      buttonColor = 'error';
      buttonVariant = 'outlined';
    }

    const handleButtonClick = async () => {
      if (isMember === NOT_PURCHASED) {
        try {
          const res = await findOriginalPlan(price_id);

          const planName = res.plan_name;
          const linkageIdentifier = res.linkage_identifier;
          const originalPlan = res.original_plan;

          if (
            !planName ||
            originalPlan?.plan?.upgrade_type === 1 ||
            originalPlan?.is_member !== 1
          ) {
            await handleStartMembership(companyId, price_id, plan_name);
            return;
          }

          showConfirm(
            `「${planName}」が解約されますがよろしいですか？`,
            async () => {
              await handleStartMembership(companyId, price_id, plan_name);
            }
          );
        } catch (error) {
          console.error('元プラン検索または解約処理でエラー', error);
        }
      } else {
        const finalLinkageIdentifier = memberId 
          ? `${linkageIdentifier}@@@${memberId}` 
          : linkageIdentifier;

        // POSTでdocomo外部システムに送るためフォーム作成
        const baseUrl = process.env.REACT_APP_REDIRECT_URL;

        const form = document.createElement('form');
        form.method = 'POST';

        form.action = baseUrl;

        const campaignInput = document.createElement('input');
        campaignInput.type = 'hidden';
        campaignInput.name = 'campaign_code';
        campaignInput.value = campaignCode || '';

        const linkageInput = document.createElement('input');
        linkageInput.type = 'hidden';
        linkageInput.name = 'linkage_identifier';
        linkageInput.value = finalLinkageIdentifier || '';

        form.appendChild(campaignInput);
        form.appendChild(linkageInput);
        document.body.appendChild(form);
        form.submit();
      }
    };

    // キャンペーンが無効で契約前の場合はボタンを非表示
    const originalPlan = originalPlans[price_id] || null;
    const isCampaignActive = campaignStatuses[price_id] !== false;
    const shouldShowButton =
      (isCampaignActive &&
        (originalPlan === null || originalPlan?.is_member !== 0)) ||
      buttonText === 'シリアルコード停止に進む';

    return (
      <>
        {shouldShowButton && (
          <Button
            onClick={handleButtonClick}
            variant={buttonVariant}
            size="small"
            color={buttonColor}
          >
            {buttonText}
          </Button>
        )}
        {isMember === PURCHASED && isSubscription && (
          <Button
            onClick={() => handleCancelMembership(linkageIdentifier, plan_name)}
            variant="outlined"
            size="small"
            color="error"
          >
            解約手続きに進む
          </Button>
        )}
        {isMember === PURCHASED && isSubscription && isCampaignActive && (
          <Button
            onClick={() => handleCardInfoChange(linkageIdentifier)}
            variant="outlined"
            size="small"
            color="success"
          >
            カード情報変更
          </Button>
        )}
      </>
    );
  };

  useEffect(() => {
    if (plansLoading || paymentsLoading) return;
    if (payments.length > 0 && !trialChecked) {
      setTrialChecked(true);
      setTimeout(() => {
        checkTrialPopup(payments);
      }, 200);
    }
  }, [plansLoading, paymentsLoading]);

  useEffect(() => {
    if (plansLoading || paymentsLoading) return;

    // 支払い情報が空の場合のみregisterPaymentIfNeedを実行
    if (payments.length === 0 && campaignCode) {
      registerPaymentIfNeed(campaignCode);
    }
  }, [
    navigate,
    plansLoading,
    paymentsLoading,
    campaignCode,
    payments.length,
    registerPaymentIfNeed,
    mutatePayments,
  ]);

  // キャンペーン有効性をチェック
  useEffect(() => {
    if (!payments.length) return;

    // 各プランのキャンペーン有効性をチェック
    payments.forEach((payment) => {
      if (payment.price_id && !(payment.price_id in campaignStatuses)) {
        checkCampaignStatus(payment.price_id);
      }
    });
  }, [payments, checkCampaignStatus, campaignStatuses]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('ログアウト失敗:', error);
    } finally {
      localStorage.clear();
      navigate('/login');
    }
  };

  const handleCardInfoChange = async (linkage_identifier) => {
    try {
      const response = await getPortalSession(linkage_identifier);

      if (response.url) {
        window.location.href = response.url;
      } else {
        showNotice('カード情報変更ページの取得に失敗しました');
      }
    } catch (error) {
      console.error('カード情報変更エラー:', error.response?.data);
    }
  };

  useEffect(() => {
    const fetchCoupons = async () => {
      if (!payments.length) return;

      try {
        const results = await Promise.all(
          payments.map(async (payment) => {
            try {
              const res = await searchCoupon(payment.linkage_identifier);

              if (!res) return [payment.linkage_identifier, null];

              const formattedValue = String(res.discount_value);

              return [
                payment.linkage_identifier,
                {
                  name: res.name,
                  discount_type: res.discount_type,
                  discount_value: formattedValue,
                  expire_date: res.expire_date,
                  datecheck: res.date_check,
                },
              ];
            } catch (err) {
              console.error('searchCoupon error:', err);
              return [payment.linkage_identifier, null];
            }
          })
        );

        const newInfo = Object.fromEntries(results);
        setCouponData(newInfo);
      } catch (error) {
        console.error('fetchCoupons error:', error);
      }
    };

    fetchCoupons();
  }, [payments]);

  if (plansLoading || userLoading) return <p>情報を読み込み中...</p>;

  return (
    <UserPageLayout
      title="マイページ"
      maxWidth="sm"
      titleActions={
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
          }}
        >
          ログアウト
        </Button>
      }
    >
      <Box
        component="div"
        sx={{
          maxWidth: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '3dvh',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="h5">お客様情報</Typography>
        <Box sx={{ py: 2 }}>
          <Stack direction="column" spacing={1}>
            <Typography variant="body1">
              <strong>氏名:</strong> {user?.name}
            </Typography>
            <Typography variant="body1">
              <strong>メールアドレス:</strong> {user?.email}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="column" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 1,
            }}
            size="small"
            onClick={() => navigate('/reset-userinformation')}
          >
            お客様情報の変更
          </Button>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 1,
            }}
            size="small"
            onClick={() => navigate('/reset-password')}
          >
            パスワードの変更
          </Button>
        </Stack>
        <Divider sx={{ mb: 3, borderBottomWidth: 2 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          契約情報
        </Typography>
        {paymentsLoading ? (
          <p>支払い情報を読み込み中...</p>
        ) : payments.length > 0 ? (
          <Stack spacing={2}>
            {payments.map((payment, index) => {
              const planName = payment.plan_name || getPlanName(payment.price_id) || '不明';
              const isCampaignActive =
                campaignStatuses[payment.price_id] !== false;
              const statusColor = !isCampaignActive
                ? 'default'
                : payment.is_member === PURCHASED
                ? 'success'
                : payment.is_member === NOT_PURCHASED
                ? 'default'
                : 'error';
              const isSubscription = getIsSubscription(payment.price_id);
              const coupon = couponData[payment.linkage_identifier];
              const is_member = getPaymentMember(
                            payment.is_member,
                            payment.price_id
                          );

              return (
                <Card key={index} sx={{ p: 2 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Stack spacing={2}>
                      {/* ヘッダー部分 */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {/* プラン情報 */}
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            契約プラン
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {planName}
                          </Typography>
                        </Box>
                        <Chip
                          label={getPaymentMember(
                            payment.is_member,
                            payment.price_id
                          )}
                          color={statusColor}
                          size="small"
                        />
                      </Box>

                      {/* 金額情報 */}
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          金額
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {getAmount(payment.price_id)}
                          {getIsSubscription(payment.price_id) ? '円/月' : '円'}
                        </Typography>
                      </Box>

                      {/* 決済番号 */}
                      {payment.linkage_identifier && (
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            決済番号
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: 'monospace',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {formatLinkageIdentifier(
                              payment.linkage_identifier,
                              null
                            )}
                          </Typography>
                        </Box>
                      )}

                      {/* 日付情報 */}
                      <Stack spacing={1}>
                        {payment.trial_ends_at && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              無料トライアル終了日
                            </Typography>
                            <Typography variant="body1">
                              {formatDateShort(payment.trial_ends_at)}
                            </Typography>
                          </Box>
                        )}

                        {getTrialDays(payment.price_id) > 0 &&
                          payment.is_member === NOT_PURCHASED && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                無料トライアル日数
                              </Typography>
                              <Typography variant="body1">
                                {`${getTrialDays(payment.price_id)}日`}
                              </Typography>
                            </Box>
                          )}

                        {payment.next_payment_day &&
                          payment.is_member === PURCHASED && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                次回支払日
                              </Typography>
                              <Typography variant="body1">
                                {formatDateShort(payment.next_payment_day)}
                              </Typography>
                            </Box>
                          )}

                          {is_member === "契約中" && coupon?.name && coupon?.datecheck && (
                            <Box
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                p: 1.5,
                                mt: 1,
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                クーポン情報
                              </Typography>

                              <Typography variant="body2">
                                <strong>クーポン名：</strong> {coupon.name}
                              </Typography>

                              <Typography variant="body2">
                                <strong>割引額：</strong> {coupon.discount_value}
                              </Typography>

                              <Typography variant="body2">
                                <strong>割引期限：</strong> {(coupon.expire_date)}
                              </Typography>

                            </Box>
                          )}

                          {/* 次回支払いから適用（scheduled_price_idがある場合） */}
                          {(payment.scheduled_price_id || payment.scheduled_plan_name) && (
                            <Box
                              sx={{
                                border: '1px solid #1976d2',
                                borderRadius: 2,
                                p: 1.5,
                                mt: 1,
                                backgroundColor: '#e3f2fd',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                sx={{ mb: 0.5 }}
                              >
                                次回支払から適用
                              </Typography>
                              {payment.scheduled_plan_name && (
                                <Typography variant="body2">
                                  <strong>契約プラン：</strong>{' '}
                                  {payment.scheduled_plan_name}
                                </Typography>
                              )}
                              {payment.scheduled_amount && (
                                <Typography variant="body2">
                                  <strong>金額：</strong> {Math.floor(payment.scheduled_amount || 0)}
                                  {getIsSubscription(payment.price_id) ? '円/月' : '円'}
                                </Typography>
                              )}
                            </Box>
                          )}

                        {payment.end_day && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              終了日
                            </Typography>
                            <Typography variant="body1">
                              {formatDateShort(payment.end_day)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {/* 操作ボタン */}
                      <Box sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
                        <Stack direction="column" spacing={1}>
                          {getPaymentButtons(
                            payment.is_member,
                            payment.campaign_code,
                            payment.linkage_identifier,
                            payment.member_id,
                            payment.company_id,
                            payment.price_id,
                            planName,
                            isSubscription
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                契約情報がありません
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
      <ConfirmModal
        open={confirmOpen}
        message={confirmMsg}
        onCancel={() => {
          setConfirmOpen(false);
          setMemberIdModalData(null);
        }}
        onConfirm={(memberId) => {
          setConfirmOpen(false);
          setMemberIdModalData(null);
          onConfirmAction(memberId);
        }}
        isMember={memberIdModalData?.isMember || false}
        planName={memberIdModalData?.planName || ''}
        validationType={memberIdModalData?.validationType || null}
        minDigits={memberIdModalData?.minDigits || null}
        maxDigits={memberIdModalData?.maxDigits || null}
        memberIdLabel={memberIdModalData?.memberIdLabel || null}
        defaultMemberId={memberIdModalData?.defaultMemberId || null}
        isPostLinked={memberIdModalData?.isPostLinked || false}  
      />
    </UserPageLayout>
  );
};

export default MyPage;
